import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { interpolateColour, getVehicleTypeColour, gradientRgb, type RgbColour, type ColourRange, DEFAULT_COLOUR } from "@/utils/colourUtils";
import { TileFeature } from "@/types/tileFeature";
import { Metric, NumericRangeDto } from "@/api/generated/models";
type ColourAccessor = (f: TileFeature) => RgbColour;
function buildColourSchemes(valueRanges: {
  speed: NumericRangeDto;
  acceleration: NumericRangeDto;
  density: NumericRangeDto;
}) {
  const mid = (r: NumericRangeDto) => (r.min + r.max) / 2;
  return {
    speed: {
      min: {
        rgb: gradientRgb.speed.min,
        value: valueRanges.speed.min
      },
      mid: {
        rgb: gradientRgb.speed.mid,
        value: mid(valueRanges.speed)
      },
      max: {
        rgb: gradientRgb.speed.max,
        value: valueRanges.speed.max
      }
    } as ColourRange,
    acceleration: {
      min: {
        rgb: gradientRgb.acceleration.min,
        value: valueRanges.acceleration.min
      },
      mid: {
        rgb: gradientRgb.acceleration.mid,
        value: mid(valueRanges.acceleration)
      },
      max: {
        rgb: gradientRgb.acceleration.max,
        value: valueRanges.acceleration.max
      }
    } as ColourRange,
    density: {
      min: {
        rgb: gradientRgb.density.min,
        value: valueRanges.density.min
      },
      mid: {
        rgb: gradientRgb.density.mid,
        value: mid(valueRanges.density)
      },
      max: {
        rgb: gradientRgb.density.max,
        value: valueRanges.density.max
      }
    } as ColourRange
  };
}
function createColourAccessors(colourSchemes: {
  speed: ColourRange;
  acceleration: ColourRange;
  density: ColourRange;
}) {
  return {
    [Metric.speed]: (f: TileFeature) => interpolateColour(f.properties.speed, colourSchemes.speed),
    [Metric.acceleration]: (f: TileFeature) => interpolateColour(f.properties.acceleration, colourSchemes.acceleration),
    [Metric.vehicleType]: (f: TileFeature) => getVehicleTypeColour(f.properties.vehicleType),
    [Metric.averageSpeed]: (f: TileFeature) => interpolateColour(f.properties.averageSpeed, colourSchemes.speed),
    [Metric.density]: (f: TileFeature) => interpolateColour(f.properties.vehicleCount, colourSchemes.density),
    [Metric.averageAcceleration]: (f: TileFeature) => interpolateColour(f.properties.averageAcceleration, colourSchemes.acceleration)
  } satisfies Record<Metric, ColourAccessor>;
}
export function useColourAccessor(metric, valueRanges) {
  const $ = _c(7);
  let t0;
  if ($[0] !== valueRanges) {
    t0 = valueRanges ? buildColourSchemes(valueRanges) : undefined;
    $[0] = valueRanges;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const colourSchemes = t0;
  let t1;
  if ($[2] !== colourSchemes) {
    t1 = colourSchemes ? createColourAccessors(colourSchemes) : undefined;
    $[2] = colourSchemes;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const accessors = t1;
  let t2;
  if ($[4] !== accessors || $[5] !== metric) {
    t2 = accessors?.[metric] ?? _temp;
    $[4] = accessors;
    $[5] = metric;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp() {
  return DEFAULT_COLOUR;
}